import { Form, Spin, Modal, Select, Button, Flex, Radio, Input } from 'antd';
import { FC, useEffect, useRef, useState } from 'react';

import { ArrowDownOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { CustomerRow, useCustomersLookUp } from '../customers/useCustomers';
import { ALIGN_BRAND_GRAY } from '../styles/colors';
import { NumericFormat } from 'react-number-format';
import { renderMaskedAccountTitle, useExternalAccounts } from '../customers/customer/useExternalAccounts';
import {
  OFFRAMP_DEST_CURRENCIES_BY_RAILS,
  OFFRAMP_DEST_RAILS,
  OFFRAMP_SOURCE_NETWORKS,
  OFFRAMP_SOURCE_TOKENS_BY_NETWORK,
} from './NewTransferModal.config';
import { Currencies, Networks, PaymentRails, useCreateTransfer } from './useTransfer';
import { useOfframpQuoteFormAware } from './useQuote';
import Big from 'big.js';
import { useQueryClient } from '@tanstack/react-query';

const parseAmount = (value: unknown): Big | null => {
  if (value === null || value === undefined || value === '') return null;
  try {
    const cleaned = String(value).replace(/,/g, '');
    return new Big(cleaned);
  } catch {
    return null;
  }
};

export const NewTransferModal: FC<{
  onClose: () => void;
  onTransferCreate: () => void;
  onTransferCreateFail: () => void;
}> = ({ onClose, onTransferCreate, onTransferCreateFail }) => {
  const [searchString, setSearchString] = useState('');
  const [focusedField, setFocusedField] = useState<'offramp.source_amount' | 'offramp.destination_amount'>(
    'offramp.source_amount'
  );
  const [editingField, setEditingField] = useState<'offramp.source_amount' | 'offramp.destination_amount' | null>(null);
  const queryClient = useQueryClient();
  const { data: customersData, isLoading } = useCustomersLookUp({
    searchString,
  });
  const [form] = Form.useForm();
  const sourceAmount = Form.useWatch('offramp.source_amount', form);
  const destinationAmount = Form.useWatch('offramp.destination_amount', form);
  const prevSourceRef = useRef(sourceAmount);
  const prevDestRef = useRef(destinationAmount);
  const customerId = Form.useWatch('customer_id', form);
  const query = useExternalAccounts(customerId);

  const { isQuoteLoading, quoteData } = useOfframpQuoteFormAware(form, editingField);

  const { isPending: isCreating, mutateAsync } = useCreateTransfer();

  const onFinish = (values: {
    customer_id: string;
    type: 'onramp' | 'offramp';
    'offramp.source_amount'?: number;
    'offramp.source_network'?: Networks;
    'offramp.source_token'?: string;
    'offramp.destination_amount'?: number;
    'offramp.destination_payment_rails'?: PaymentRails;
    'offramp.destination_currency'?: Currencies;
    'offramp.destination_external_account_id'?: string;
  }) => {
    mutateAsync({
      payload: {
        customer_id: values.customer_id,
        quote_id: quoteData.offramp.quote_id,
        transfer_type: values.type,
        offramp:
          values.type === 'offramp'
            ? {
                destination_external_account_id: values['offramp.destination_external_account_id']!,
              }
            : undefined,
      },
    })
      .then(() => {
        onTransferCreate();
        onClose();
        return queryClient.invalidateQueries({
          queryKey: ['transfers'],
        });
      })
      .catch(() => {
        onTransferCreateFail();
      });
  };

  useEffect(() => {
    const currentAmount = parseAmount(sourceAmount);
    const prevAmount = parseAmount(prevSourceRef.current);

    if (
      focusedField === 'offramp.source_amount' &&
      currentAmount !== null &&
      (prevAmount === null || !currentAmount.eq(prevAmount))
    ) {
      console.log({
        sourceAmount,
        prevSourceRef: prevSourceRef.current,
      });
      setEditingField('offramp.source_amount');
    }
    prevSourceRef.current = sourceAmount;
  }, [sourceAmount, focusedField]);

  useEffect(() => {
    const currentAmount = parseAmount(destinationAmount);
    const prevAmount = parseAmount(prevDestRef.current);

    if (
      focusedField === 'offramp.destination_amount' &&
      currentAmount !== null &&
      (prevAmount === null || !currentAmount.eq(prevAmount))
    ) {
      setEditingField('offramp.destination_amount');
    }
    prevDestRef.current = destinationAmount;
  }, [destinationAmount, focusedField]);

  return (
    <Modal
      loading={isCreating}
      title="Create New Transfer"
      width={600}
      destroyOnClose
      footer={null}
      open
      onCancel={onClose}
    >
      <Form
        onFinish={onFinish}
        name="personal_details"
        form={form}
        style={{
          paddingTop: '22px',
        }}
        requiredMark={false}
        initialValues={{
          type: 'offramp',
          'offramp.source_network': OFFRAMP_SOURCE_NETWORKS[0],
          'offramp.source_token': OFFRAMP_SOURCE_TOKENS_BY_NETWORK[OFFRAMP_SOURCE_NETWORKS[0]][0],
          'offramp.destination_payment_rails': OFFRAMP_DEST_RAILS[0],
          'offramp.destination_currency': OFFRAMP_DEST_CURRENCIES_BY_RAILS[OFFRAMP_DEST_RAILS[0]][0],
        }}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Please select customer',
            },
          ]}
          name="customer_id"
          layout="vertical"
          label="Customer"
        >
          <Select
            showSearch
            placeholder="Search customers..."
            loading={isLoading}
            filterOption={false}
            onSearch={setSearchString}
            notFoundContent={isLoading ? 'Loading...' : 'No customers found'}
            options={(customersData?.items || []).map((customer: CustomerRow) => ({
              value: customer.id,
              label:
                customer.beneficiary_type === 'individual'
                  ? `${customer.first_name} ${customer.last_name} (${customer.id})`
                  : `${customer.company_name} (${customer.id})`,
            }))}
          />
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Please enter',
            },
          ]}
          name="type"
          layout="vertical"
          label="Transfer type"
        >
          <Radio.Group
            className={css`
              display: flex;
            `}
            size="middle"
          >
            <Radio.Button
              className={css`
                text-align: center;
                flex: 1;
              `}
              value="onramp"
            >
              Onramp
            </Radio.Button>
            <Radio.Button
              className={css`
                text-align: center;
                flex: 1;
              `}
              value="offramp"
            >
              Offramp
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item shouldUpdate noStyle>
          {() => {
            const type = form.getFieldValue('type');

            if (type === 'offramp') {
              return (
                <Flex gap="middle" vertical>
                  <Flex
                    vertical
                    gap="small"
                    className={css`
                      position: relative;
                      background-color: #fbfaf6;
                      padding: 16px;
                      border-radius: 6px;
                    `}
                  >
                    <span
                      className={css`
                        font-weight: bold;
                        font-size: 12px;
                        color: ${ALIGN_BRAND_GRAY.MutedTaupe};
                      `}
                    >
                      FROM
                    </span>
                    <Flex gap="middle">
                      <Form.Item
                        className={css`
                          flex: 1;
                        `}
                        name="offramp.source_amount"
                        layout="vertical"
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const type = getFieldValue('type');
                              if (type !== 'offramp') return Promise.resolve();

                              if (!value) {
                                return Promise.reject('Amount is required');
                              }

                              const numValue = parseFloat(String(value).replace(/,/g, ''));
                              if (numValue < 1 || numValue > 10_000_000) {
                                return Promise.reject('Amount must be between 1 and 10,000,000');
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                        label="Amount"
                      >
                        <NumericFormat
                          readOnly={isQuoteLoading}
                          className={css`
                            background-color: #fbfaf6 !important;
                          `}
                          customInput={Input}
                          placeholder="1-10,000,000"
                          thousandSeparator=","
                          allowNegative={false}
                          decimalScale={2}
                          min={1}
                          max={10000000}
                          onFocus={() => setFocusedField('offramp.source_amount')}
                        />
                      </Form.Item>

                      <Form.Item
                        className={css`
                          flex: 1;
                        `}
                        name="offramp.source_network"
                        layout="vertical"
                        label="Network"
                      >
                        <Select
                          onChange={(newNetwork: Networks) => {
                            const currentToken = form.getFieldValue('offramp.source_token');
                            const newTokens = OFFRAMP_SOURCE_TOKENS_BY_NETWORK[newNetwork] || [];

                            // If current token is not available in new network, preselect first token
                            if (!newTokens.includes(currentToken) && newTokens.length > 0) {
                              form.setFieldValue('offramp.source_token', newTokens[0]);
                            }
                          }}
                          className={css`
                            .ant-select-selector {
                              background-color: #fbfaf6 !important;
                            }
                          `}
                          placeholder="Select value..."
                        >
                          {OFFRAMP_SOURCE_NETWORKS.map((network) => (
                            <Select.Option key={network} value={network}>
                              {network.toUpperCase()}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        className={css`
                          flex: 1;
                        `}
                        noStyle
                        dependencies={['offramp.source_network']}
                      >
                        {({ getFieldValue }) => {
                          const network = getFieldValue('offramp.source_network') as Networks;
                          const tokens = network ? OFFRAMP_SOURCE_TOKENS_BY_NETWORK[network] : [];
                          return (
                            <Form.Item
                              className={css`
                                flex: 1;
                              `}
                              name="offramp.source_token"
                              layout="vertical"
                              label="Token"
                            >
                              <Select
                                className={css`
                                  .ant-select-selector {
                                    background-color: #fbfaf6 !important;
                                  }
                                `}
                                placeholder="Select value..."
                              >
                                {tokens?.map((token) => (
                                  <Select.Option key={token} value={token}>
                                    {token.toUpperCase()}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Flex>

                    <Flex
                      className={css`
                        position: absolute;
                        border-radius: 50%;
                        background-color: #fff;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 38px;
                        height: 38px;
                        bottom: -26px;
                        z-index: 1;
                        left: 50%;
                        margin-left: -19px;
                        box-shadow: 0 1px 4px ${ALIGN_BRAND_GRAY.BoneWhite};
                      `}
                      justify="center"
                    >
                      {isQuoteLoading ? (
                        <Spin
                          size="small"
                          className={css`
                            .ant-spin-dot-item {
                              background-color: ${ALIGN_BRAND_GRAY.MutedTaupe};
                            }
                          `}
                        />
                      ) : (
                        <ArrowDownOutlined
                          className={css`
                            font-size: 18px;
                            color: ${ALIGN_BRAND_GRAY.MutedTaupe};
                          `}
                        />
                      )}
                    </Flex>
                  </Flex>

                  <Flex
                    vertical
                    gap="small"
                    className={css`
                      padding: 16px;
                      background-color: #fbfaf6;
                      border-radius: 6px;
                    `}
                  >
                    <span
                      className={css`
                        font-weight: bold;
                        font-size: 12px;
                        color: ${ALIGN_BRAND_GRAY.MutedTaupe};
                      `}
                    >
                      TO
                    </span>
                    <Flex gap="middle">
                      <Form.Item
                        className={css`
                          flex: 1;
                        `}
                        name="offramp.destination_amount"
                        layout="vertical"
                        label="Amount"
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const type = getFieldValue('type');
                              if (type !== 'offramp') return Promise.resolve();

                              if (!value) {
                                return Promise.reject('Amount is required');
                              }

                              const numValue = parseFloat(String(value).replace(/,/g, ''));
                              if (numValue < 1 || numValue > 10_000_000) {
                                return Promise.reject('Amount must be between 1 and 10,000,000');
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <NumericFormat
                          className={css`
                            background-color: #fbfaf6 !important;
                          `}
                          customInput={Input}
                          placeholder="1-10,000,000"
                          thousandSeparator=","
                          allowNegative={false}
                          decimalScale={2}
                          min={1}
                          max={10000000}
                          readOnly={isQuoteLoading}
                          onFocus={() => setFocusedField('offramp.destination_amount')}
                        />
                      </Form.Item>

                      <Form.Item
                        className={css`
                          flex: 1;
                        `}
                        name="offramp.destination_payment_rails"
                        layout="vertical"
                        label="Rails"
                      >
                        <Select
                          onChange={(newRails: PaymentRails) => {
                            const destination_currency = form.getFieldValue('offramp.destination_currency');
                            const newCurrs = OFFRAMP_DEST_CURRENCIES_BY_RAILS[newRails] || [];

                            // If current token is not available in new network, preselect first token
                            if (!newCurrs.includes(destination_currency) && newCurrs.length > 0) {
                              form.setFieldValue('offramp.destination_currency', newCurrs[0]);
                            }
                          }}
                          className={css`
                            .ant-select-selector {
                              background-color: #fbfaf6 !important;
                            }
                          `}
                          placeholder="Select value..."
                        >
                          {OFFRAMP_DEST_RAILS.map((rail) => (
                            <Select.Option key={rail} value={rail}>
                              {rail === PaymentRails.INTERNATIONAL_WIRE ? 'SWIFT' : rail.toUpperCase()}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        className={css`
                          flex: 1;
                        `}
                        noStyle
                        dependencies={['offramp.destination_payment_rails']}
                      >
                        {({ getFieldValue }) => {
                          const rail = getFieldValue('offramp.destination_payment_rails') as PaymentRails;
                          const currencies = rail ? OFFRAMP_DEST_CURRENCIES_BY_RAILS[rail] : [];
                          return (
                            <Form.Item
                              className={css`
                                flex: 1;
                              `}
                              name="offramp.destination_currency"
                              layout="vertical"
                              label="Currency"
                            >
                              <Select
                                className={css`
                                  .ant-select-selector {
                                    background-color: #fbfaf6 !important;
                                  }
                                `}
                                placeholder="Select value..."
                              >
                                {currencies?.map((c) => (
                                  <Select.Option key={c} value={c}>
                                    {c.toUpperCase()}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Flex>
                  </Flex>

                  <Form.Item
                    name="offramp.destination_external_account_id"
                    layout="vertical"
                    label="Destination Account"
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const type = getFieldValue('type');
                          if (type === 'offramp' && !value) {
                            return Promise.reject('Destination Account is required');
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select an account"
                      loading={isLoading}
                      filterOption={false}
                      onSearch={setSearchString}
                      options={(query?.data?.items || []).map((acc) => ({
                        value: acc.id,
                        label: renderMaskedAccountTitle(acc),
                      }))}
                    />
                  </Form.Item>
                </Flex>
              );
            }

            return <>coming soon</>;
          }}
        </Form.Item>

        <Flex style={{ paddingTop: '18px' }} gap={'middle'} justify="flex-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit">
            Create Transfer
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
